<script setup lang="ts">
/**
 * Obraz produktu z fallbackiem na neutralny placeholder "brak zdjęcia".
 * Pokazuje placeholder gdy `src` jest pusty (produkt bez zdjęcia)
 * lub gdy obraz nie załaduje się (404 — plik usunięty z serwera).
 *
 * Wszystkie atrybuty (width/height/format/loading/fetchpriority/sizes/class)
 * są przekazywane na <NuxtImg> przez $attrs; klasa pozycjonująca rodzica
 * trafia też na placeholder, więc zachowuje aspect-ratio/object-fit slotu.
 */
import { isBrokenImg } from "~~/app/utils/imgFade";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  src?: string | null;
  alt?: string;
}>();

const hasError = ref(false);
const imgRef = ref<{ $el?: unknown } | null>(null);

watch(
  () => props.src,
  () => {
    hasError.value = false;
    // Nowy src może być już w cache jako zepsuty (404) — `@error` wtedy nie
    // poleci ponownie, więc po renderze sprawdź stan obrazka ręcznie.
    nextTick(detectBrokenImg);
  },
);

const showPlaceholder = computed(() => !props.src || hasError.value);
const placeholderLabel = computed(() => props.alt || "Brak zdjęcia");

function onError() {
  hasError.value = true;
}

/**
 * Wyłapuje 404, które poleciało ZANIM Vue podpięło `@error` (SSR: serwer renderuje
 * <img>, przeglądarka pobiera go i dostaje 404 przed hydracją → zdarzenie `error`
 * przepada, `@error` nigdy nie strzela). Bez tego placeholder LAVMAG nie pokazałby
 * się dla zdjęć powyżej folda (loading="eager"), które nie istnieją na serwerze.
 */
function detectBrokenImg() {
  const root = imgRef.value?.$el;
  const img =
    root instanceof HTMLImageElement
      ? root
      : root instanceof Element
        ? root.querySelector("img")
        : null;
  if (img && isBrokenImg(img)) hasError.value = true;
}

onMounted(detectBrokenImg);
</script>

<template>
  <NuxtImg
    v-if="!showPlaceholder"
    ref="imgRef"
    :src="src ?? undefined"
    :alt="alt"
    v-bind="$attrs"
    @error="onError"
  />
  <div
    v-else
    class="product-image-placeholder"
    :class="$attrs.class"
    :style="$attrs.style"
    role="img"
    :aria-label="placeholderLabel"
  >
    <svg
      class="product-image-placeholder__icon"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="6"
        y="9"
        width="36"
        height="30"
        rx="3.5"
        stroke="currentColor"
        stroke-width="1.6"
      />
      <circle
        cx="16.5"
        cy="19"
        r="3.25"
        stroke="currentColor"
        stroke-width="1.6"
      />
      <path
        d="M9 33.5l8.5-8.5 5.5 5.5 7-7 8 8"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>
</template>

<style scoped>
/* Podwojona klasa podbija specyficzność (0,3,0), żeby centrowanie zawsze wygrało
   z klasą rodzica wstrzykiwaną przez $attrs.class — np. `.product-card__img`
   ustawia `display: block; object-fit: cover`, co przy remisie specyficzności
   (0,2,0) mogło wygrać zależnie od kolejności w bundlu i przesunąć ikonę
   do lewego górnego rogu zamiast wyśrodkować ją w slocie. */
.product-image-placeholder.product-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  /* delikatny gradient zamiast czystej bieli — slot czyta się jako celowy
     placeholder, nie jako pusty/zepsuty obraz */
  background: linear-gradient(
    150deg,
    var(--color-bg) 0%,
    var(--color-surface) 100%
  );
  color: var(--color-text-muted);
  container-type: inline-size;
  overflow: hidden;
}

/* Zaokrąglenie sterowane brandem (#2): LAVMAG 0, youlook 15px. */
:deep(img),
.product-image-placeholder.product-image-placeholder {
  border-radius: var(--product-img-radius);
}

.product-image-placeholder__icon {
  flex-shrink: 0;
  /* duża, wyśrodkowana ikona; karta produktu → ~50% szerokości slotu,
     duży slot PDP → do 180px */
  width: clamp(48px, 50cqw, 180px);
  height: auto;
  opacity: 0.7;
}

/* Bardzo wąskie sloty (miniatury koszyka/zamówień) — mniejsza ikona */
@container (max-width: 72px) {
  .product-image-placeholder__icon {
    width: 60cqw;
    opacity: 0.5;
  }
}
</style>
